import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, ImagePlus, Loader2, Palette, RotateCcw, Trash2, Upload, X } from 'lucide-react';
import { useEditMode } from '../../context/EditModeContext.jsx';
import { useTenant } from '../../context/TenantContext.jsx';
import { useContentBlocks } from '../../lib/content.js';
import { applyBrandOverrides } from '../../lib/utils.js';
import {
  BACKGROUND_SLOTS,
  DEFAULT_FONT_ID,
  DEFAULT_PRIMARY_COLOR,
  DEFAULT_SECONDARY_COLOR,
  FONT_OPTIONS,
  PRIMARY_SWATCHES,
  SECONDARY_SWATCHES,
  clampStrength,
  contrastGrade,
  contrastRatio,
  fontIdFromValue,
  getFontOption,
  normalizeHexColor,
  readBackgroundSlot,
} from '../../lib/design-options.js';
import {
  backgroundClearBlocks,
  backgroundSetBlocks,
  backgroundStrengthBlocks,
  createCommitGate,
  describeSaveError,
  saveContentBlocks,
  saveDesign,
  uploadBackgroundImage,
  validateImageFile,
} from '../../lib/design.js';
import { HERO_OPACITY_VAR } from './HeroBackdrop.jsx';

/**
 * DesignDrawer — Design anpassen (Issue #3 Paket B).
 *
 * Seitenpanel für eingeloggte tenant_admins (canEdit). Farben, Schriften und das
 * Hero-Hintergrundbild werden live in der Storefront angezeigt und beim
 * Bestätigen sofort gespeichert (Picker schließen, Hex mit Enter/Blur, Auswahl,
 * Upload fertig, Regler loslassen). Es gibt keinen Entwurf- oder
 * Veröffentlichen-Zustand: was die Storefront zeigt, ist gespeichert.
 *
 * Das Panel ist bewusst nicht modal: Shop und Inline-Texte bleiben bedienbar,
 * während der Drawer offen ist.
 */

const SECTION_IDS = {
  colors: 'design-section-colors',
  fonts: 'design-section-fonts',
  background: 'design-section-background',
};

const NAV_ITEMS = [
  { key: 'colors', label: 'Farben' },
  { key: 'fonts', label: 'Schrift' },
  { key: 'background', label: 'Bild' },
];

/**
 * Springt im Scroll-Bereich des Drawers zu einem Bereich (ohne Animation bei „weniger Bewegung“).
 * Bewusst KEIN scrollIntoView: Es würde auch das overflow-hidden-Panel selbst verschieben und
 * Titel und Navigation aus dem Blick schieben.
 */
function scrollToSection(scroller, key) {
  const target = document.getElementById(SECTION_IDS[key]);
  if (!scroller || !target) return;
  const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  const top = target.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - 12;
  scroller.scrollTo?.({ top: Math.max(0, top), behavior: reduce ? 'auto' : 'smooth' });
}

function formatRatio(ratio) {
  return typeof ratio === 'number' ? ratio.toFixed(1).replace('.', ',') : '–';
}

// Native 'change' (Farbwähler geschlossen / Regler losgelassen). Reacts onChange
// feuert dagegen bei jedem 'input' und eignet sich nur für die Live-Vorschau.
// `mounted` kennzeichnet, ob das Element gerade im DOM steht (bedingt gerenderte
// Elemente wie der Stärke-Regler): beim Auftauchen wird der Listener neu gebunden.
function useNativeChange(ref, handler, mounted = true) {
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  });
  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const listener = (event) => handlerRef.current(event);
    element.addEventListener('change', listener);
    return () => element.removeEventListener('change', listener);
  }, [ref, mounted]);
}

function SectionHeading({ id, children, action }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <h3 id={`${id}-title`} className="text-xs font-bold uppercase tracking-widest text-slate-500">
        {children}
      </h3>
      {action}
    </div>
  );
}

function ResetLink({ onClick, testId, children = 'Auf Standard' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 underline-offset-2 hover:text-slate-900 hover:underline cursor-pointer"
    >
      <RotateCcw className="h-3 w-3" aria-hidden="true" />
      {children}
    </button>
  );
}

function ColorField({ testId, label, hint, value, defaultValue, swatches, onPreview, onCommit }) {
  const [draft, setDraft] = useState(value);
  const [problem, setProblem] = useState('');
  const pickerRef = useRef(null);
  // Vergleicht gegen den zuletzt ANGEFRAGTEN Wert: Rückweg während eines laufenden Speicherns zählt.
  const gateRef = useRef(null);
  if (!gateRef.current) gateRef.current = createCommitGate(value);
  const valueRef = useRef(value);

  useEffect(() => {
    valueRef.current = value;
    gateRef.current.sync(value);
    setDraft(value);
    setProblem('');
  }, [value]);

  const commit = useCallback(
    (hex) => {
      gateRef.current.commit(hex, async (next) => {
        const ok = await onCommit(next);
        // Speichern fehlgeschlagen: das Eingabefeld zeigt wieder den gespeicherten Wert (die Seite tut das schon).
        if (ok === false) setDraft(valueRef.current);
        return ok;
      });
    },
    [onCommit],
  );

  useNativeChange(pickerRef, (event) => commit(normalizeHexColor(event.target.value)));

  const shown = normalizeHexColor(draft) || value;
  const ratio = contrastRatio(shown, '#ffffff');
  const grade = contrastGrade(ratio);
  const gradeText = {
    good: 'gut lesbar auf Weiß',
    fair: 'nur für große Flächen und Schrift',
    low: 'zu schwach auf Weiß',
  }[grade];

  return (
    <div className="space-y-2">
      <div>
        <label htmlFor={`${testId}-hex`} className="block text-sm font-semibold text-slate-900">
          {label}
        </label>
        <p className="text-xs text-slate-500">{hint}</p>
      </div>

      <div className="flex items-center gap-2">
        <input
          ref={pickerRef}
          type="color"
          value={shown}
          aria-label={`${label} wählen`}
          data-testid={`${testId}-picker`}
          onChange={(event) => {
            setDraft(event.target.value);
            onPreview(event.target.value);
          }}
          className="h-9 w-12 cursor-pointer rounded-md border border-slate-300 bg-white p-0.5"
        />
        <input
          id={`${testId}-hex`}
          type="text"
          value={draft}
          maxLength={7}
          spellCheck={false}
          autoComplete="off"
          data-testid={`${testId}-hex`}
          onChange={(event) => {
            const next = event.target.value;
            setDraft(next);
            setProblem('');
            // Vorschau erst bei vollständigem Wert, sonst flackert die Seite beim Tippen.
            if (/^#?[0-9a-f]{6}$/i.test(next.trim())) onPreview(normalizeHexColor(next));
          }}
          onBlur={() => {
            const hex = normalizeHexColor(draft);
            if (hex) {
              setDraft(hex);
              commit(hex);
            } else {
              setDraft(value);
              onPreview(value);
              if (draft.trim()) setProblem('Bitte einen Hex-Wert wie #e3000b eingeben.');
            }
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.currentTarget.blur();
          }}
          className="h-9 w-28 rounded-md border border-slate-300 bg-white px-2 font-mono text-sm uppercase text-slate-900 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/20"
        />
        {value !== defaultValue && (
          <ResetLink
            testId={`${testId}-reset`}
            onClick={() => {
              onPreview(defaultValue);
              gateRef.current.commit(defaultValue, () => onCommit(null));
            }}
          />
        )}
      </div>

      {problem && (
        <p role="alert" className="text-xs font-medium text-red-700">
          {problem}
        </p>
      )}

      <div className="flex flex-wrap gap-1.5" role="group" aria-label={`${label}: Vorschläge`}>
        {swatches.map((swatch) => {
          const selected = swatch.value === value;
          return (
            <button
              key={swatch.value}
              type="button"
              title={swatch.label}
              aria-label={`${label}: ${swatch.label}`}
              aria-pressed={selected}
              data-testid={`${testId}-swatch-${swatch.value.slice(1)}`}
              onClick={() => {
                onPreview(swatch.value);
                commit(swatch.value);
              }}
              style={{ backgroundColor: swatch.value }}
              className={`h-9 w-9 cursor-pointer rounded-full border border-black/10 transition-transform hover:scale-110 sm:h-7 sm:w-7 ${
                selected ? 'ring-2 ring-slate-900 ring-offset-2' : ''
              }`}
            />
          );
        })}
      </div>

      <p
        data-testid={`${testId}-contrast`}
        data-grade={grade}
        className={`text-xs font-medium ${grade === 'good' ? 'text-emerald-700' : grade === 'fair' ? 'text-amber-700' : 'text-red-700'}`}
      >
        Kontrast {formatRatio(ratio)}:1, {gradeText}
      </p>
    </div>
  );
}

function FontField({ testId, label, hint, value, allowInherit, onChange }) {
  const selectedHint = FONT_OPTIONS.find((font) => font.id === value)?.hint;
  return (
    <div className="space-y-1.5">
      <label htmlFor={testId} className="block text-sm font-semibold text-slate-900">
        {label}
      </label>
      <select
        id={testId}
        data-testid={testId}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-9 w-full cursor-pointer rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/20"
      >
        {allowInherit && <option value="">Wie Schriftart (Standard)</option>}
        {FONT_OPTIONS.map((font) => (
          <option key={font.id} value={font.id}>
            {font.label}
            {!allowInherit && font.id === DEFAULT_FONT_ID ? ' (Standard)' : ''}
          </option>
        ))}
      </select>
      <p className="text-xs text-slate-500" data-testid={`${testId}-hint`}>
        {selectedHint ? `${selectedHint}. ` : ''}
        {hint}
      </p>
    </div>
  );
}

function BackgroundField({ slot, tenant, revision, onPersist, onChanged, run }) {
  const { map } = useContentBlocks(slot.page, tenant.slug, { revision });
  const { imageUrl, strength } = readBackgroundSlot(map, slot);
  const [liveStrength, setLiveStrength] = useState(strength);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [fileProblem, setFileProblem] = useState('');
  const fileRef = useRef(null);
  const sliderRef = useRef(null);
  const strengthRef = useRef(strength);
  // Gate: 36 -> 35 während 36 noch gespeichert wird, darf den Rückweg nicht verschlucken.
  const strengthGate = useRef(null);
  if (!strengthGate.current) strengthGate.current = createCommitGate(strength);

  useEffect(() => {
    strengthRef.current = strength;
    strengthGate.current.sync(strength);
    setLiveStrength(strength);
  }, [strength]);

  useNativeChange(
    sliderRef,
    (event) => {
      const next = clampStrength(event.target.value, slot);
      strengthGate.current.commit(next, () =>
        onPersist(backgroundStrengthBlocks(slot, next), {
          onError: () => {
            document.documentElement.style.removeProperty(HERO_OPACITY_VAR);
            setLiveStrength(strengthRef.current);
          },
        }),
      );
    },
    Boolean(imageUrl),
  );

  const handleFile = async (file) => {
    setFileProblem('');
    const problem = validateImageFile(file);
    if (problem) {
      setFileProblem(problem);
      return;
    }
    setUploading(true);
    try {
      await run(async () => {
        const url = await uploadBackgroundImage(file);
        await saveContentBlocks(tenant.id, slot.page, backgroundSetBlocks(slot, url));
        onChanged();
      });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3" data-testid={`design-bg-${slot.id}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-slate-900">{slot.label}</p>
        {imageUrl && (
          <button
            type="button"
            data-testid={`design-bg-${slot.id}-remove`}
            onClick={() => onPersist(backgroundClearBlocks(slot, { includeStrength: true }))}
            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-red-700 cursor-pointer"
          >
            <Trash2 className="h-3 w-3" aria-hidden="true" />
            Entfernen
          </button>
        )}
      </div>

      <div
        onDragOver={(event) => {
          event.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragOver(false);
          const file = event.dataTransfer?.files?.[0];
          if (file) handleFile(file);
        }}
        className={`relative flex h-28 items-center justify-center overflow-hidden rounded-lg border-2 border-dashed ${
          dragOver ? 'border-slate-900 bg-slate-100' : 'border-slate-300 bg-slate-50'
        }`}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt="Aktuelles Hintergrundbild"
            data-testid={`design-bg-${slot.id}-thumb`}
            className="h-full w-full object-cover"
          />
        ) : (
          <p className="px-3 text-center text-xs text-slate-500">
            Kein Bild. Ziehen Sie ein Foto hierher oder laden Sie eins hoch.
          </p>
        )}
        {uploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80" role="status">
            <Loader2 className="h-5 w-5 animate-spin text-slate-700" aria-label="Bild wird hochgeladen" />
          </div>
        )}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="sr-only"
        tabIndex={-1}
        aria-label="Hintergrundbild auswählen"
        data-testid={`design-bg-${slot.id}-input`}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) handleFile(file);
        }}
      />
      <button
        type="button"
        disabled={uploading}
        data-testid={`design-bg-${slot.id}-upload`}
        onClick={() => fileRef.current?.click()}
        className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-md bg-slate-900 px-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-wait disabled:opacity-60 cursor-pointer"
      >
        <Upload className="h-4 w-4" aria-hidden="true" />
        {imageUrl ? 'Bild ersetzen' : 'Bild hochladen'}
      </button>
      <p className="text-xs text-slate-500">JPG, PNG oder WebP. Große Fotos werden automatisch verkleinert.</p>
      {fileProblem && (
        <p role="alert" className="text-xs font-medium text-red-700">
          {fileProblem}
        </p>
      )}

      {imageUrl && (
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <label htmlFor={`design-bg-${slot.id}-strength`} className="text-sm font-semibold text-slate-900">
              Bildstärke
            </label>
            <span className="font-mono text-xs text-slate-600" data-testid={`design-bg-${slot.id}-strength-value`}>
              {liveStrength} %
            </span>
          </div>
          <input
            ref={sliderRef}
            id={`design-bg-${slot.id}-strength`}
            type="range"
            min={slot.minStrength}
            max={slot.maxStrength}
            step={1}
            value={liveStrength}
            data-testid={`design-bg-${slot.id}-strength`}
            onChange={(event) => {
              const next = clampStrength(event.target.value, slot);
              setLiveStrength(next);
              document.documentElement.style.setProperty(HERO_OPACITY_VAR, String(next / 100));
            }}
            className="w-full cursor-pointer accent-slate-900"
          />
          <p className="text-xs text-slate-500">Niedriger = heller Schleier, Texte bleiben gut lesbar.</p>
        </div>
      )}
    </div>
  );
}

function DesignPanel({ tenant }) {
  const { applyDesign } = useTenant();
  const { designSection, closeDesign, bumpRevision, revision } = useEditMode();

  const [status, setStatus] = useState({ state: 'idle', message: '' });
  const [confirmReset, setConfirmReset] = useState(false);
  const panelRef = useRef(null);
  const scrollRef = useRef(null);
  const queueRef = useRef(Promise.resolve());
  const pendingRef = useRef(0);
  const errorRef = useRef('');

  const saved = useMemo(
    () => ({
      primaryColor: normalizeHexColor(tenant.primaryColor) || DEFAULT_PRIMARY_COLOR,
      secondaryColor: normalizeHexColor(tenant.secondaryColor) || DEFAULT_SECONDARY_COLOR,
      fontFamily: fontIdFromValue(tenant.settings?.font_family),
      fontHeading: fontIdFromValue(tenant.settings?.font_heading),
    }),
    [tenant.primaryColor, tenant.secondaryColor, tenant.settings?.font_family, tenant.settings?.font_heading],
  );
  const savedRef = useRef(saved);
  useEffect(() => {
    savedRef.current = saved;
  }, [saved]);

  // Auswahl sofort zeigen (optimistisch); der Server-Stand zieht nach dem Speichern nach.
  const [fontDraft, setFontDraft] = useState({
    family: saved.fontFamily || DEFAULT_FONT_ID,
    heading: saved.fontHeading || '',
  });
  useEffect(() => {
    setFontDraft({ family: saved.fontFamily || DEFAULT_FONT_ID, heading: saved.fontHeading || '' });
  }, [saved.fontFamily, saved.fontHeading]);

  // Fokus ins Panel (Tastaturbedienung) und ggf. zur angesprungenen Section scrollen.
  useEffect(() => {
    panelRef.current?.focus({ preventScroll: true });
  }, []);

  // Schließen gibt den Fokus an den Auslöser in der Toolbar zurück (kein „Fokus im Nichts“).
  const handleClose = useCallback(() => {
    closeDesign();
    setTimeout(() => document.querySelector('[data-testid="edit-toolbar-design"]')?.focus?.(), 0);
  }, [closeDesign]);
  useEffect(() => {
    if (SECTION_IDS[designSection]) scrollToSection(scrollRef.current, designSection);
  }, [designSection]);

  // Beim Schließen nicht gespeicherte Vorschau verwerfen (außer ein Speichern läuft noch).
  useEffect(
    () => () => {
      if (pendingRef.current === 0) {
        applyBrandOverrides(savedRef.current);
        document.documentElement.style.removeProperty(HERO_OPACITY_VAR);
      }
    },
    [],
  );

  // Alle Schreibzugriffe laufen seriell: schnelle Folgeänderungen landen in der
  // Reihenfolge der Bedienung, die letzte gewinnt.
  const run = useCallback((task, { onError } = {}) => {
    // Neue Bedienung (nichts mehr in der Warteschlange): alter Fehler ist erledigt.
    if (pendingRef.current === 0) errorRef.current = '';
    pendingRef.current += 1;
    setStatus({ state: 'saving', message: '' });
    const job = queueRef.current.then(async () => {
      try {
        await task();
        if (pendingRef.current === 1) {
          // Ein früherer Fehler derselben Bedienung wird nicht von einem späteren Erfolg überschrieben.
          setStatus(errorRef.current ? { state: 'error', message: errorRef.current } : { state: 'saved', message: '' });
        }
        return true;
      } catch (error) {
        if (onError) onError(error);
        errorRef.current = describeSaveError(error);
        setStatus({ state: 'error', message: errorRef.current });
        return false;
      } finally {
        pendingRef.current -= 1;
      }
    });
    queueRef.current = job;
    return job;
  }, []);

  const preview = useCallback((partial) => {
    applyBrandOverrides({ ...savedRef.current, ...partial });
  }, []);

  const persistDesign = useCallback(
    (patch) =>
      run(
        async () => {
          const design = await saveDesign(tenant.id, patch);
          applyDesign({
            primaryColor: design.primaryColor,
            secondaryColor: design.secondaryColor,
            fontFamily: design.fontFamily,
            fontHeading: design.fontHeading,
          });
        },
        {
          onError: () => {
            applyBrandOverrides(savedRef.current);
            setFontDraft({
              family: savedRef.current.fontFamily || DEFAULT_FONT_ID,
              heading: savedRef.current.fontHeading || '',
            });
          },
        },
      ),
    [run, tenant.id, applyDesign],
  );

  const persistBlocks = useCallback(
    (blocks, { onError } = {}) =>
      run(
        async () => {
          await saveContentBlocks(tenant.id, BACKGROUND_SLOTS[0].page, blocks);
          bumpRevision();
        },
        { onError },
      ),
    [run, tenant.id, bumpRevision],
  );

  const resetAll = useCallback(async () => {
    setConfirmReset(false);
    applyBrandOverrides({ primaryColor: DEFAULT_PRIMARY_COLOR, secondaryColor: DEFAULT_SECONDARY_COLOR });
    const designReset = await persistDesign({ primary_color: null, secondary_color: null, font_family: null, font_heading: null });
    // Fehlgeschlagen: Fehlermeldung stehen lassen, Hintergrund nicht halb zurücksetzen.
    if (!designReset) return;
    await persistBlocks(BACKGROUND_SLOTS.flatMap((slot) => backgroundClearBlocks(slot, { includeStrength: true })));
  }, [persistDesign, persistBlocks]);

  const bodyStack = getFontOption(fontDraft.family)?.stack;
  const headingStack = getFontOption(fontDraft.heading || fontDraft.family)?.stack;

  return (
    <div
      ref={panelRef}
      id="design-drawer"
      role="dialog"
      aria-modal="false"
      aria-labelledby="design-drawer-title"
      tabIndex={-1}
      data-testid="design-drawer"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          handleClose();
        }
      }}
      className="fixed inset-x-2 bottom-20 z-[61] flex max-h-[68vh] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white text-slate-900 shadow-2xl outline-none sm:inset-x-auto sm:bottom-24 sm:right-4 sm:top-[104px] sm:max-h-none sm:w-[360px]"
    >
      <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
        <div className="flex items-start gap-2.5">
          <Palette className="mt-0.5 h-5 w-5 text-slate-700" aria-hidden="true" />
          <div>
            <h2 id="design-drawer-title" className="text-base font-bold leading-tight">
              Design anpassen
            </h2>
            <p className="text-xs text-slate-500">Änderungen sind sofort im Shop sichtbar.</p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleClose}
          aria-label="Design-Bereich schließen"
          data-testid="design-close"
          className="rounded-full p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900 cursor-pointer"
        >
          <X className="h-4 w-4" />
        </button>
      </header>

      <nav aria-label="Bereiche im Design-Drawer" className="flex gap-1 border-b border-slate-200 px-3 py-1.5">
        {NAV_ITEMS.map((item) => (
          <button
            key={item.key}
            type="button"
            data-testid={`design-nav-${item.key}`}
            onClick={() => scrollToSection(scrollRef.current, item.key)}
            className="cursor-pointer rounded-full px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 hover:text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-slate-900"
          >
            {item.label}
          </button>
        ))}
      </nav>

      <div ref={scrollRef} data-testid="design-scroll" className="scroll-shadows flex-1 space-y-6 overflow-y-auto px-4 py-4">
        <section id={SECTION_IDS.colors} aria-labelledby={`${SECTION_IDS.colors}-title`} className="space-y-4">
          <SectionHeading id={SECTION_IDS.colors}>Farben</SectionHeading>
          <ColorField
            testId="design-primary"
            label="Hauptfarbe"
            hint="Buttons, Links und Hervorhebungen. Die Schrift auf Buttons wird automatisch hell oder dunkel."
            value={saved.primaryColor}
            defaultValue={DEFAULT_PRIMARY_COLOR}
            swatches={PRIMARY_SWATCHES}
            onPreview={(hex) => preview({ primaryColor: hex })}
            onCommit={(hex) => persistDesign({ primary_color: hex })}
          />
          <ColorField
            testId="design-secondary"
            label="Zweitfarbe"
            hint="Zusatztexte und Details, zum Beispiel Unterzeilen."
            value={saved.secondaryColor}
            defaultValue={DEFAULT_SECONDARY_COLOR}
            swatches={SECONDARY_SWATCHES}
            onPreview={(hex) => preview({ secondaryColor: hex })}
            onCommit={(hex) => persistDesign({ secondary_color: hex })}
          />
        </section>

        <section id={SECTION_IDS.fonts} aria-labelledby={`${SECTION_IDS.fonts}-title`} className="space-y-3">
          <SectionHeading
            id={SECTION_IDS.fonts}
            action={
              (saved.fontFamily || saved.fontHeading) && (
                <ResetLink
                  testId="design-fonts-reset"
                  onClick={() => {
                    setFontDraft({ family: DEFAULT_FONT_ID, heading: '' });
                    preview({ fontFamily: null, fontHeading: null });
                    persistDesign({ font_family: null, font_heading: null });
                  }}
                />
              )
            }
          >
            Schrift
          </SectionHeading>
          <FontField
            testId="design-font-family"
            label="Schriftart"
            hint="Gilt für alle Texte im Shop."
            value={fontDraft.family}
            onChange={(id) => {
              setFontDraft((draft) => ({ ...draft, family: id }));
              preview({ fontFamily: id });
              persistDesign({ font_family: id });
            }}
          />
          <FontField
            testId="design-font-heading"
            label="Überschriften"
            hint="Optional eine eigene Schrift nur für Überschriften."
            value={fontDraft.heading}
            allowInherit
            onChange={(id) => {
              setFontDraft((draft) => ({ ...draft, heading: id }));
              preview({ fontHeading: id || null });
              persistDesign({ font_heading: id || null });
            }}
          />
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="design-font-preview">
            <p className="text-lg font-bold leading-snug" style={{ fontFamily: headingStack }}>
              Sicherheitstechnik direkt finden
            </p>
            <p className="mt-1 text-sm text-slate-600" style={{ fontFamily: bodyStack }}>
              Gezielte Artikelsuche für Errichter und Fachplaner. 0123456789 € ä ö ü ß
            </p>
          </div>
        </section>

        <section id={SECTION_IDS.background} aria-labelledby={`${SECTION_IDS.background}-title`} className="space-y-3">
          <SectionHeading id={SECTION_IDS.background}>
            <span className="inline-flex items-center gap-1.5">
              <ImagePlus className="h-3.5 w-3.5" aria-hidden="true" />
              Hintergrundbild
            </span>
          </SectionHeading>
          {BACKGROUND_SLOTS.map((slot) => (
            <BackgroundField
              key={slot.id}
              slot={slot}
              tenant={tenant}
              revision={revision}
              run={run}
              onPersist={persistBlocks}
              onChanged={bumpRevision}
            />
          ))}
        </section>
      </div>

      <footer className="space-y-2 border-t border-slate-200 px-4 py-3">
        <p
          role="status"
          aria-live="polite"
          data-testid="design-status"
          data-state={status.state}
          className={`flex min-h-5 items-center gap-1.5 text-xs font-medium ${
            status.state === 'error' ? 'text-red-700' : status.state === 'saved' ? 'text-emerald-700' : 'text-slate-500'
          }`}
        >
          {status.state === 'saving' && (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Speichert …
            </>
          )}
          {status.state === 'saved' && (
            <>
              <Check className="h-3.5 w-3.5" aria-hidden="true" /> Gespeichert und live
            </>
          )}
          {status.state === 'error' && (
            <>
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> {status.message}
            </>
          )}
          {status.state === 'idle' && 'Änderungen werden sofort gespeichert.'}
        </p>

        {confirmReset ? (
          <div className="flex items-center gap-2" data-testid="design-reset-confirm-row">
            <button
              type="button"
              onClick={resetAll}
              data-testid="design-reset-confirm"
              className="h-8 flex-1 rounded-md bg-red-700 px-3 text-xs font-bold text-white hover:bg-red-800 cursor-pointer"
            >
              Ja, alles zurücksetzen
            </button>
            <button
              type="button"
              onClick={() => setConfirmReset(false)}
              data-testid="design-reset-cancel"
              className="h-8 rounded-md border border-slate-300 px-3 text-xs font-semibold text-slate-700 hover:bg-slate-100 cursor-pointer"
            >
              Abbrechen
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmReset(true)}
            data-testid="design-reset-all"
            className="inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-md border border-slate-300 px-3 text-xs font-semibold text-slate-700 hover:bg-slate-100 cursor-pointer"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
            Alles auf Standard zurücksetzen
          </button>
        )}
      </footer>
    </div>
  );
}

export function DesignDrawer() {
  const { canEdit, designOpen } = useEditMode();
  const { tenant } = useTenant();
  if (!canEdit || !designOpen || !tenant?.id) return null;
  return <DesignPanel tenant={tenant} />;
}
